import { useEffect, useState } from "react";
import type { Tab } from "./lib/types";
import { useLeakTest, type Mode } from "./lib/useLeakTest";
import type { ScenarioKey } from "./lib/scenarios";
import { IDLE_TINT } from "./lib/verdict";
import { Hero } from "./components/Hero";
import { LookupScene } from "./components/LookupScene";
import { YourConnection } from "./components/YourConnection";
import { LeakDiagram } from "./components/LeakDiagram";
import { Explainers } from "./components/Explainers";
import { Footer } from "./components/Footer";

function readParams(): { mode: Mode; scenario: ScenarioKey; autoRun: boolean } {
  const q = new URLSearchParams(typeof location !== "undefined" ? location.search : "");
  const s = q.get("scenario");
  const scenario: ScenarioKey = s === "secure" || s === "novpn" || s === "leak" ? s : "leak";
  // Live measurement is the default. Simulation (the design's demo scenarios)
  // only when explicitly requested via ?scenario= or ?mode=sim.
  const mode: Mode = q.get("mode") === "sim" || s ? "sim" : "live";
  return { mode, scenario, autoRun: q.get("autorun") === "1" };
}

export default function App() {
  const [{ mode, scenario, autoRun }] = useState(readParams);
  const [tab, setTab] = useState<Tab>("ip");
  const test = useLeakTest(mode, scenario);
  const { phase, servers, count, lit } = test;
  const lookupCounter = phase === "running" ? `lookup ${Math.min(lit + 1, count)} / ${count}` : phase === "done" ? `${lit} / ${count} ${test.stopped ? "stopped" : "complete"}` : `0 / ${count}`;

  useEffect(() => {
    if (autoRun) {
      const id = setTimeout(() => test.run(), 700);
      return () => clearTimeout(id);
    }
    // run-once on mount; test.run identity is stable enough for this intent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun]);

  const heroTint = IDLE_TINT;

  return (
    <div style={{ minHeight: "100vh", background: "#000000", color: "#DEDFDF", position: "relative", overflowX: "hidden" }}>
      <div
        style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 640, pointerEvents: "none",
          backgroundImage: "radial-gradient(circle, rgba(222,223,223,0.09) 1px, transparent 1.3px)",
          backgroundSize: "22px 22px",
          WebkitMaskImage: "radial-gradient(520px 320px at 50% 230px, black, transparent 72%)",
          maskImage: "radial-gradient(520px 320px at 50% 230px, black, transparent 72%)",
        }}
      />
      <div
        style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 640, pointerEvents: "none",
          background: `radial-gradient(560px 360px at 50% 230px, ${heroTint}, transparent 70%)`,
          transition: "background 600ms cubic-bezier(0.2,0,0,1)",
        }}
      />

      <div data-rt-main="" style={{ position: "relative", maxWidth: 880, margin: "0 auto", padding: "0 24px 72px 24px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Hero phase={phase} count={count} runMs={test.runMs} lit={lit} found={test.found} stopped={test.stopped} onRun={test.run} onStop={test.stop} />

        <div data-results={phase === "idle" ? "hide" : "show"} style={{ width: "100%", display: "flex", flexDirection: "column", gap: 14, paddingTop: 24 }}>
          <div data-rt-card="" style={{ background: "#0A0B0A", border: "1px solid #2A2D29", borderRadius: 26, padding: "22px 26px", display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B9BCB6" }}>Live Lookups</span>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#5C5F5A" }}>{lookupCounter}</span>
            </div>
            <LookupScene
              phase={phase}
              now={test.now}
              fired={test.fired}
              nameservers={test.nameservers}
              pings={test.pings}
              nsCounts={test.nsCounts}
              reducedMotion={test.reducedMotion}
              vw={test.vw}
            />
          </div>

        </div>

        <YourConnection connection={test.connection} tab={tab} onTab={setTab} servers={servers} phase={phase} />
        <LeakDiagram servers={servers} phase={phase} reducedMotion={test.reducedMotion} />
        <Explainers />
        <Footer />
      </div>
    </div>
  );
}
